import moment from 'moment';
import { ActionContext, Module } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import ElgaPatient from '@/types/soap/elga/Patient';
import Karteieintrag from '@/types/Karteieintrag';
import Patient from '@/types/Patient';
import Rezepteintrag from '@/types/Rezepteintrag';
import User from '@/types/User';
import Warteliste from '@/types/Warteliste';
import api from '@/services/Api';
import { Satzart } from '@/types/Satzart';
import { errorSwal, successSwal, toastSwal, warningSwal } from '@/helper/swals';
import { patientimpfungen } from '@/store/patientimpfungen';
import { patientrechnung } from '@/store/patientrechnungen';
import { patientreiseplaene } from '@/store/patientreiseplaene';
import {
    BelegLoaders,
    BriefLoaders,
    ElgaLoaders,
    ExtendedPatientState, PatientLoaders,
    PatientState, PrintPayload,
    RootState,
    UntersuchungLoaders,
} from '@/store/types';
import Impfung from '@/types/Impfung';
import { CardInfo } from '@/types/soap/elga/Card';
import Reiseplan, { PlanItem } from '@/types/Reiseplan';
import Planimpfung from '@/types/Planimpfung';
import PatUntersuchung from '@/types/PatUntersuchung';
import { DataResponse } from '@/types';
import Beleg from '@/types/Beleg';
import {SystemEinstellung, SystemEinstellungKey} from '@/types/Einstellung';

export type UpdateUntersuchungPayload = { data: Partial<Karteieintrag>; closeDialog: boolean; anforderungsdruck?: boolean };
export type UebertragenPatientPayload = { uebertraeger: number; uebernehmer: number }
export type CreateTextPayload = { text: Karteieintrag; files: File | File[] | null };
export type PatientContactPayload = { patnr?: string; svnr?: string };
export type PrintPatientenkarteiPayload = {
    patnr: number;
    options: number;
    impfstelle_id: number;
    export?: boolean;
    name?: string;
};

type LoadCardInfoData = DataResponse<{ card: Partial<CardInfo>; patienten: Partial<Patient>[]; onlyInfo?: any }>;

type Context = ActionContext<PatientState, RootState>;

export const patient: Module<PatientState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            patientDetail: false,
            patientNew: false,
            impfungNew: false,
            reiseplan: false,
            rechnung: false,
            druckok: false,
            addAgainToWartelisteDialog: false,
            druckansicht: false,
            texteintrag: false,
            rezept: false,
            leistung: false,
            diagnose: false,
            anamnese: false,
            anamneselistitem: true,
            brief: false,
            impfungEdit: false,
            impfungNachtrag: false,
            zahlungart: false,
            druckhinweis: false,
            patientDruckDetail: false,
            patientEtikettenDetail: false,
            elgaPatient: false,
            elgaSuche: false,
            patientuebertragung: false,
            ssnoSearch: false,
            ecard: false,
            ecardInfo: false,
            dekurs: false,
            untersuchung: false,
            warteliste: false,
            zurKassa: false,
            sammelrechnung: false
        },
        loaders: {
            impfung: false,
            impfungundneu: false,
            impfungImpferinnerung: false,
            impfungErinnerungGesendetReset: false,
            impfungAbrechnung: false,
            untersuchungAbrechnung: false,
            erinnerungen: false,
            untersuchungen: false,
            reiseplan: false,
            patient: false,
            patientuebertragung: false,
            patientloeschung: false,
            reiseplaene: false,
            reiseplandelete: false,
            reiseplanprint: false,
            distinctBefundwerttexte: false,
            text: {
                save: false,
                delete: false,
                download: false,
                downloads: [],
            },
            rezepteintrag: {
                save: false,
                print: false,
                export: false,
                delete: false,
            },
            leistung: {
                save: false,
                delete: false,
            },
            diagnose: {
                save: false,
                delete: false,
            },
            anamnese: {
                save: false,
                delete: false,
                print: false,
            },
            brief: {
                save: false,
                saveAndPrint: false,
                print: false,
                export: false,
                delete: false,
            },
            untersuchung: {
                save: false,
                printBefund: false,
                printUntersuchung: false,
                printBefunde: false,
                printUntersuchungen: false,
                delete: false,
                email: false,
                einsendeformular: false,
                untersuchungsanforderung: false,
            },
            warteliste: {
                save: false,
            },
            beleg: {
                save: false,
                storno: false,
                print: false,
                export: false,
                exportXml: false,
                abschluss: false,
                berichtigung: false,
                mahnung: false,
            },
            druck: false,
            delete: false,
            etiketten: false,
            export: false,
            exportPdf: false,
            folgetermin: false,
            elga: {
                patient: false,
                save: false,
                save_ohne_adresse: false,
                uebertragung: false,
                stornierung: false,
                search: false,
            },
            ecard: {
                info: false,
            },
            ecardInfo: {
                info: false,
            },
            dekurs: {
                save: false,
            },
        },
        patientMitAdresse: false,
        showAllePatientInfo: true,
        patientMitAdresseUndPlan: false,
        patientData: null,
        loadedPatientData: null,
        elgaPatient: new ElgaPatient(),
        elgaPatienten: [],
        elgaPatientAnlage: new ElgaPatient(),
        tempPatientData: new Patient(),
        patientDataNew: new Patient(),
        selected_text: new Karteieintrag(),
        selected_rezepteintrag: new Karteieintrag(),
        selected_leistungseintrag: new Karteieintrag(),
        selected_brief: new Karteieintrag(),
        selected_untersuchung: new Karteieintrag(),
        selected_diagnose: new Karteieintrag(),
        selected_anamnese: new Karteieintrag(),
        selected_offen: [],
        cardInfo: null,
        patientLastAnamnese: new Impfung(),
        cardInfoPatienten: [],
        zahlendePatienten: [],
        untersuchungen: [],
        patienten: [],
        distinctBefundwerttexte: [],
        ...patientreiseplaene.state,
        ...patientrechnung.state,
        ...patientimpfungen.state,
    },
    actions: {
        ...patientrechnung.actions,
        ...patientreiseplaene.actions,
        ...patientimpfungen.actions,
        loadDistinctBefundwerttexte({ commit, state }: Context): void {
            commit('setLoaders', { distinctBefundwerttexte: true });

            api.post('ivs/patienten/loadDistinctBefundwerttexte')
                .then((response: AxiosResponse) => {
                    commit('updateDistinctBefundwerttexte', response.data.body);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { distinctBefundwerttexte: false }));
        },
        patientDetail({ commit }: Context, payload: boolean): void {
            commit('patientDetail', payload);
        },
        patientNew({ commit, dispatch, rootState }: Context, visible: boolean): void {
            const user = rootState.authentication.user;
            if (!user?.elga_access || !visible) {
                commit('patientNew', visible);
                return;
            }

            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            if (!arbeitsplatz_id) {
                setTimeout(() => commit('globals/setDialogArbeitsplatzhinweis', true, { root: true }), 100);
                return;
            }

            commit('setLoadersEcard', { info: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                arbeitsplatz_id,
            };

            api.post('ivs/cards/info', data)
                .then(({ data }: AxiosResponse<LoadCardInfoData>) => {
                    if (data.body.patienten.length === 0) {
                        dispatch('patientContact', { svnr: data.body.card.number });
                    }

                    commit('setCardInfo', data);
                })
                .catch((error) => {
                    console.error(error);

                    const errorResponse = typeof error.response?.data?.body === 'string'
                        ? error.response?.data?.body
                        : null;

                    toastSwal('error', 'e-Card', errorResponse ?? 'Daten konnten nicht abgerufen werden');

                    commit('patientNew', true);
                })
                .finally(() => commit('setLoadersEcard', { info: false }));
        },
        patientNeuMitAdresse({ commit }: Context, payload: boolean): void {
            commit('setPatientMitAdresse');
            commit('patientNeuMitAdresse', payload);
        },
        patientNeuMitAdresseUndPlan({ commit }: Context, payload: boolean): void {
            commit('setPatientMitAdresseUndPlan');
            commit('patientNeuMitAdresseUndPlan', payload);
        },
        patientDruck({ commit }: Context, payload: boolean): void {
            commit('patientDruck', payload);
        },
        patientEtiketten({ commit }: Context, payload: boolean): void {
            commit('patientEtiketten', payload);
        },
        patientFolgetermin({ commit, state }: Context): void {
            commit('setLoaders', { folgetermin: true });

            const data = {
                patnr: state.patientData?.patnr,
                folgetermin: state.patientData?.hat_folgetermin,
            };

            api.post('ivs/patienten/folgetermin', data)
                .then((response: AxiosResponse) => {
                    commit('updatePatientData', response.data.body);
                    successSwal('Patient', 'gespeichert');
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { folgetermin: false }));
        },
        printEtiketten({ commit, state, dispatch, rootGetters, rootState }: Context, payload: PrintPayload): void {
            commit('setLoaders', { etiketten: true });

            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            const data = {
                patnr: state.patientData?.patnr,
                paperName: payload.paperName,
                variante: getSystemEinstellung('etikettvariante').value ?? '',
                impfstelle_id: rootState.authentication.impfstelle_id,
            };

            api.post('ivs/etiketten', data, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    commit('patientEtiketten', false);
                    dispatch('globals/printEtiketten', { data: response.data, ...payload }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { etiketten: false }));
        },
        loadPatient({ commit, dispatch, rootState, rootGetters }: Context, { patnr, onlyReload }: { patnr: number, onlyReload?: boolean }): Promise<void> {
            commit('setFilters', {
                satzarten: Satzart.values(),
                elga: false,
            });

            commit('setLoadersPatient', true);
            dispatch('patientContact', { patnr });

            return api.post('ivs/patienten/load', { patnr })
                .then((response: AxiosResponse) => {
                    const patient = new Patient(response.data.body);
                    const user = rootState.authentication.user as User | null;
                    const onlyreload = onlyReload ?? false;

                    const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                        rootGetters['authentication/getSystemEinstellung'];

                   /* const filterReisePlan = {
                        aktuelle: 'n'
                    };*/

                    dispatch('closePatient');
                    commit('updatePatientData', patient);
                    commit('patientDetail', false);
                    dispatch('getReiseplaene');
                    dispatch('loadRechnungen');
                    dispatch('loadImpfungen');
                    dispatch('loadUntersuchungen');
                    if (getSystemEinstellung('muw').isEnabled()) {
                        dispatch('loadLastAnamnese', {patnr: patnr ?? patient.patnr});
                    }
                    dispatch('loadErinnerungen');
                    dispatch('warteliste/updateWarteliste', null, { root: true });

                    if (!onlyreload) {
                        if (patient.hasValidSvnr && user?.elga_access) {
                            const action = patient.bpk_gh ? 'autoSyncImpfpass' : 'autoFindImpfpass';
                            dispatch(action, patient);
                        }
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersPatient', false));
        },
        getLoadedPatient({ commit, dispatch, rootState }: Context, { patnr }: { patnr: number }): Promise<void> {
            commit('setFilters', {
                satzarten: Satzart.values(),
                elga: false,
            });

            commit('setLoadersPatient', true);

            return api.post('ivs/patienten/load', { patnr })
                .then((response: AxiosResponse) => {
                    const patient = new Patient(response.data.body);
                    const user = rootState.authentication.user as User | null;

                    commit('updateLoadedPatientData', patient);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersPatient', false));
        },
        loadPatienten({ commit, dispatch, rootState }: Context, payload?): void {
            api.post('ivs/patienten/loadPatientenwithOffeneSalden', payload)
                .then((response: AxiosResponse) => {
                    commit('setPatienten', response.data.body);
                })
                .catch(console.error);
        },
        checkImpfpass({ dispatch, rootState }: Context, payload: Patient): void {
            const user = rootState.authentication.user;
            const patient = new Patient(payload);

            if (!patient.bpk_gh && patient.hasValidSvnr && user?.elga_access) {
                dispatch('autoFindImpfpass', patient);
            }
        },
        patientContact({ rootState }: Context, payload: PatientContactPayload): void {
            const user = rootState.authentication.user;
            const arbeitsplatz_id = rootState.session.arbeitsplatz;

            if (user?.elga_access && arbeitsplatz_id) {
                api.post('ivs/cards/contact', { ...payload, arbeitsplatz_id })
                    .then((response: AxiosResponse) => console.log('patientContact:', response.data.body))
                    .catch((error: AxiosError) => {
                        console.error(error);
                        toastSwal('error', 'e-Card', 'Daten konnten nicht abgerufen werden');
                    });
            }
        },
        updatePatient({ commit, dispatch }: Context, payload: Patient): void {
            commit('setLoadersPatient', true);

            api.post('ivs/patienten/update', payload)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        commit('updatePatientData', response.data.body);
                        dispatch('checkImpfpass', response.data.body);
                        dispatch('warteliste/loadWarteliste', null, { root: true });
                        commit('patientDetail', false);
                        successSwal('Patient', 'gespeichert');
                    } else {
                        warningSwal('Fehler', response.data.body);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersPatient', false));
        },
        createPatient({ commit, dispatch }: Context, payload: Patient): void {
            commit('setLoadersPatient', true);
            dispatch('closePatient');

            api.post('ivs/patienten/create', payload)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        commit('updatePatientData', response.data.body);
                        dispatch('checkImpfpass', response.data.body);
                        commit('patientNew', false);
                        dispatch('getReiseplaene');
                        dispatch('loadRechnungen');
                        dispatch('loadImpfungen');
                        commit('setPatientLastAnamnese', null);
                        successSwal('Patient', 'gespeichert');
                    } else {
                        warningSwal('Fehler', response.data.body);
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersPatient', false);
                    commit('setPatientMitAdresseUndPlan', false);
                });
        },
        deletePatient({ commit, dispatch }: Context, payload: { patnr: number }): void {
            commit('setLoaders', { patientloeschung: true });

            api.post('ivs/patienten/delete', payload)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Löschung', response.data.body ?? 'Patient ist nicht löschbar.');
                    } else {
                        successSwal('Patient', 'gelöscht');
                        commit('patientDetail', false);
                        dispatch('closePatient');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { patientloeschung: false }));
        },
        uebertragenPatient({ commit, dispatch }: Context, payload: UebertragenPatientPayload): void {
            commit('setLoaders', { patientuebertragung: true });

            api.post('ivs/patienten/transfer', payload)
                .then(() => {
                    commit('setDialogPatientUebertragung', false);
                    commit('patientDetail', false);
                    dispatch('getReiseplaene');
                    dispatch('loadRechnungen');
                    dispatch('loadImpfungen');
                    successSwal('Patient', 'übertragen');
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { patientuebertragung: false }));
        },
        closePatient(context: Context): void {
            const state = context.state as ExtendedPatientState;
            state.patientData = null;
            state.cardInfo = null;
            state.cardInfoPatienten = [];
            state.selected_offen = [];
            state.untersuchungen = [];

            state.reiseplaene = [];
            state.reiseplanimpfungen = [];
            state.reiseplanuntersuchungen = [];
            state.selected_reiseplan = new Reiseplan();
            state.selected_offeneImpfung = null;

            state.druckvorgang = false;
            state.druckvorgangRuecksetzung = false;
            state.abbrechungsvorgang = false;
            state.abbrechungsvorgangAlle = false;
            state.loadRechnung = false;
            state.beleg = new Beleg();
            state.belegPositionen = [];
            state.belegPatienten = [];
            state.rechnungen = [];
            state.rechnungenpositionen = [];
            state.rechnungenpatienten = [];
            state.selected_rechnung = new Beleg();

            state.patientImpfungNew = new Impfung();
            state.impfungen = [];
            state.erinnerungen = [];
            state.filter = {
                satzarten: Satzart.values(),
                elga: false,
                gruppierung: false,
            };
            state.dt_load = false;
            state.loadingText = false;
            state.selected_impfeintrag = new Karteieintrag();
            state.gruppiert = false;
        },
        createText({ commit, dispatch }: Context, { text, files }: CreateTextPayload): void {
            commit('setLoadersText', { save: true });

            const data = new FormData();

            const serialize = (root: string, obj: Record<string, unknown | null>): void => {
                Object.entries(obj).forEach(([key, value]) => {
                    if (typeof value === 'object' && value !== null) {
                        serialize(`${root}[${key}]`, value as unknown as Record<string, unknown | null>);
                    } else {
                        data.append(`${root}[${key}]`, value === null ? '' : (value as string).toString());
                    }
                });
            };

            serialize('text', text as unknown as Record<string, unknown | null>);

            if (files) {
                data.append('files[]', files as Blob);
            }

            api.post('ivs/patimpfungen/text', data)
                .then(() => {
                    dispatch('loadImpfungen');
                    commit('setDialogText', false);
                    successSwal('Text', 'gespeichert');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersText', { save: false }));
        },
        createRezepteintrag({ commit, dispatch }: Context, payload: Rezepteintrag): void {
            commit('setLoadersRezept', { save: true });

            api.post('ivs/patimpfungen/createrezepteintrag', payload)
                .then((response: AxiosResponse) => {
                    commit('setDialogRezept', false);
                    successSwal('Rezepteintrag', 'gespeichert');
                    dispatch('printRezepteintrag', response.data.body);
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersRezept', { save: false });
                    dispatch('loadImpfungen');
                });
        },
        createLeistungseintrag({ commit, dispatch }: Context, payload: Impfung): void {
            commit('setLoadersLeistung', { save: true });

            api.post('ivs/patimpfungen/createleistungseintrag', payload)
                .then(() => {
                    commit('setDialogLeistung', false);
                    successSwal('Leistungseintrag', 'gespeichert');
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersLeistung', { save: false });
                    dispatch('loadImpfungen');
                });
        },
        createDiagnoseneintrag({ commit, dispatch }: Context, payload: Impfung): void {
            commit('setLoadersDiagnose', { save: true });

            api.post('ivs/patimpfungen/creatediagnoseneintrag', payload)
                .then(() => {
                    commit('setDialogDiagnose', false);
                    successSwal('Diagnose', 'gespeichert');
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersDiagnose', { save: false });
                    dispatch('loadPatient', {patnr: payload.patnr, onlyReload: true});
                });
        },
        createAnamneseneintrag({ commit, dispatch }: Context, {data, print}: {data: Impfung; print:boolean}): void {
            if (print) {
                commit('setLoadersAnamnese', {print: true});
            } else {
                commit('setLoadersAnamnese', {save: true});
            }

            api.post('ivs/patimpfungen/createanamneseneintrag', data)
                .then((response: AxiosResponse) => {
                        if (!print) {
                            commit('setDialogAnamnese', false);
                        }
                    successSwal('Anamnese', 'gespeichert');

                    if (print) {
                        dispatch('printAnamnese', response.data.body);
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersAnamnese', { save: false });
                    dispatch('loadImpfungen');
                    dispatch('loadLastAnamnese', {patnr: data.patnr});
                });
        },
        updateBrief({ commit, dispatch }: Context,
                    { data, print }: { data: Partial<Karteieintrag>; print: boolean }): void {
            commit('setLoadersBrief', { [print ? 'saveAndPrint' : 'save']: true });

            const isNew = !!data.patimpfung_id;

            api.post('ivs/patimpfungen/brief', data)
                .then((response: AxiosResponse) => {
                    successSwal('Brief', 'gespeichert');

                    if (!isNew || print) {
                        commit('setDialogBrief', false);
                    }

                    if (print) {
                        dispatch('printBrief', response.data.body);
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersBrief', { [print ? 'saveAndPrint' : 'save']: false });

                    if (!isNew || print) {
                        dispatch('loadImpfungen');
                    }
                });
        },
        updateUntersuchung({ commit, dispatch, state, rootGetters }: Context, { data, closeDialog, anforderungsdruck }: UpdateUntersuchungPayload): void {
            commit('setLoadersUntersuchung', { save: true });

            api.post('ivs/patimpfungen/untersuchung', data)
                .then((response: AxiosResponse) => {
                    successSwal('Untersuchung', 'gespeichert');

                    if (response.data.body.isarray) {
                        const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                            rootGetters['authentication/getSystemEinstellung'];

                        const muw = getSystemEinstellung('muw').isEnabled();

                        if (muw) {
                            const payload = {
                                untersuchungen: response.data.body.untersuchungen,
                                patient: state.patientData,
                                onlytoday: false
                            };

                            dispatch('printUntersuchungsanforderung', payload);
                        }

                        if (response.data.body.untersuchungen.length === 1) {
                            commit('setSelectedUntersuchung', response.data.body.untersuchungen[0]);
                        }
                    } else {
                        commit('setSelectedUntersuchung', response.data.body);
                    }

                    if (closeDialog) {
                        commit('setDialogUntersuchung', false);
                    }
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersUntersuchung', { save: false });

                    if (closeDialog) {
                        dispatch('loadImpfungen');
                        dispatch('getReiseplaene');
                        dispatch('loadUntersuchungen');
                    }
                });
        },
        deleteText({ state, commit, dispatch }: Context, payload?: Karteieintrag): void {
            commit('setLoadersText', { delete: true });

            const { patimpfung_id } = payload ?? state.selected_text;

            api.post('ivs/patimpfungen/delete', { patimpfung_id })
                .then(() => {
                    dispatch('loadImpfungen');
                    commit('setDialogText', false);
                    successSwal('Text', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersText', { delete: false }));
        },
        downloadTextDatei({ state, commit }: Context, payload?: Karteieintrag): void {
            const { patimpfung_id, datei } = payload ?? state.selected_text;
            const fromKartei = state.selected_text.patimpfung_id === null;

            if (fromKartei) {
                commit('setLoadersTextDownloads', { patimpfung_id, download: true });
            } else {
                commit('setLoadersText', { download: true });
            }

            api.post('ivs/text/download', { patimpfung_id }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    if (response.data.type === 'text/html') {
                        response.data.text().then((text: string) => {
                            const data = JSON.parse(text);
                            errorSwal('Text Download', data.body);
                        });
                    } else {
                        saveAs(response.data, datei ?? 'datei.bin');
                    }
                })
                .catch(console.error)
                .finally(() => {
                    if (fromKartei) {
                        commit('setLoadersTextDownloads', { patimpfung_id, download: false });
                    } else {
                        commit('setLoadersText', { download: false });
                    }
                });
        },
        deleteRezepteintrag({ commit, dispatch }: Context, payload: Rezepteintrag): void {
            commit('setLoadersRezept', { delete: true });

            api.post('ivs/patimpfungen/deleterezepteintrag', payload)
                .then(() => {
                    dispatch('loadImpfungen');
                    commit('setDialogRezept', false);
                    successSwal('Rezepteintrag', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersRezept', { delete: false }));
        },
        deleteLeistungseintrag({ commit, dispatch }: Context, payload: Impfung): void {
            commit('setLoadersLeistung', { delete: true });

            api.post('ivs/patimpfungen/deleteleistungseintrag', payload)
                .then(() => {
                    dispatch('loadImpfungen');
                    commit('setDialogLeistung', false);
                    successSwal('Leistungseintrag', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersLeistung', { delete: false }));
        },
        deleteDiagnoseneintrag({ commit, dispatch }: Context, payload: Impfung): void {
            commit('setLoadersDiagnose', { delete: true });

            api.post('ivs/patimpfungen/deletediagnoseneintrag', payload)
                .then(() => {
                    dispatch('loadPatient', {patnr: payload.patnr, onlyReload: true});
                    commit('setDialogDiagnose', false);
                    successSwal('Diagnose', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersDiagnose', { delete: false });
                });
        },
        deleteAnamneseneintrag({ commit, dispatch }: Context, payload: Impfung): void {
            commit('setLoadersAnamnese', { delete: true });

            api.post('ivs/patimpfungen/deleteanamneseneintrag', payload)
                .then(() => {
                    dispatch('loadImpfungen');
                    commit('setDialogAnamnese', false);
                    successSwal('Anamnese', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => {
                    commit('setLoadersAnamnese', { delete: false });
                });
        },
        deleteBrief({ state, commit, dispatch }: Context): void {
            commit('setLoadersBrief', { delete: true });

            const data = {
                patimpfung_id: state.selected_brief.patimpfung_id,
            };

            api.post('ivs/patimpfungen/delete', data)
                .then(() => {
                    dispatch('loadImpfungen');
                    commit('setDialogBrief', false);
                    successSwal('Brief', 'gelöscht');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { delete: false }));
        },
        deleteUntersuchung({ state, commit, dispatch }: Context): void {
            commit('setLoadersUntersuchung', { delete: true });

            const data = {
                patimpfung_id: state.selected_untersuchung.patimpfung_id,
            };

            api.post('ivs/patimpfungen/delete', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        dispatch('loadImpfungen');
                        dispatch('getReiseplaene');
                        dispatch('loadUntersuchungen');
                        commit('setDialogUntersuchung', false);
                        successSwal('Untersuchung', 'gelöscht');
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Diese Untersuchung ist nicht löschbar.');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersUntersuchung', { delete: false }));
        },
        printUntersuchungsanforderung({ commit, dispatch, rootState }: Context, payload: { patient: Patient, untersuchungen: PatUntersuchung[], onlytoday: boolean}): void {
            commit('setLoadersUntersuchungLoaders', { untersuchungsanforderung: true });

            api.post('ivs/untersuchungsanforderungdruck', payload, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                        dispatch('globals/printDokument', { data }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersUntersuchungLoaders', { untersuchungsanforderung: false }));
        },
        printPatientenkartei({ commit, dispatch }: Context, payload: PrintPatientenkarteiPayload): void {
            const isExport = payload.export;
            const patientName = payload.name;

            commit('setLoaders', { [isExport ? 'exportPdf' : 'druck']: true });

            api.post('ivs/patientenkarteidruck', payload, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    if (isExport) {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        saveAs(data, `Patientenkartei_${patientName}_${date}.pdf`);
                    } else {
                        dispatch('globals/printDokument', { data }, { root: true });
                        commit('patientDruck', false);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { [isExport ? 'exportPdf' : 'druck']: false }));
        },
        exportPatientenkartei({ commit, rootGetters }: Context,
                              payload: { patnr: number; options: number; impfstelle_id: number }): void {
            commit('setLoaders', { export: true });

            api.post('ivs/patientenkarteidruck/export', payload, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Karteiblatt_${payload.patnr}_${impfstelle}_${date}.xml`);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { export: false }));
        },
        printRezepteintrag({ commit, dispatch, rootGetters }: Context, { patimpfung_id }: Rezepteintrag): void {
            commit('setLoadersRezept', { print: true });

            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            const rezeptFormular = getSystemEinstellung('rezeptformular').value;

            console.log(rezeptFormular);

            api.get(`ivs/rezepteintrag/index?patimpfung_id=${patimpfung_id}&rezeptformular=${rezeptFormular}`, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printRezept', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoadersRezept', { print: false }));
        },
        exportRezepteintrag({ commit, state, rootGetters }: Context, { patimpfung_id }: Rezepteintrag): void {
            commit('setLoadersRezept', { export: true });

            api.post('ivs/rezepteintrag/index', { patimpfung_id }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const patientName = state.patientData?.name?.replace(/ /g, '_');
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Rezept_${patientName}_${impfstelle}_${date}.pdf`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersRezept', { export: false }));
        },
        printBrief({ state, commit, dispatch }: Context, payload?: Karteieintrag): void {
            commit('setLoadersBrief', { print: true });

            const patimpfung_id = payload?.patimpfung_id ?? state.selected_brief.patimpfung_id;

            api.post('ivs/brief', { patimpfung_id, export: false }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { print: false }));
        },
        printAnamnese({ state, commit, dispatch }: Context, payload?: Karteieintrag): void {
            commit('setLoadersAnamnese', { print: true });

            //const patimpfung_id = payload?.patimpfung_id ?? state.selected_anamnese.patimpfung_id;

            api.post('ivs/anamnese', { payload }, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    dispatch('globals/printDokument', { data }, { root: true });
                    commit('setDialogAnamnese', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersAnamnese', { print: false }));
        },
        exportBrief({ state, commit, rootGetters }: Context): void {
            commit('setLoadersBrief', { export: true });

            const patimpfung_id = state.selected_brief.patimpfung_id;
            const patientName = state.patientData?.name?.replace(/ /g, '_');

            api.post('ivs/brief', { patimpfung_id, export: true }, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Arztbrief_${patientName}_${impfstelle}_${date}.pdf`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersBrief', { export: false }));
        },
        printUntersuchung({ state, commit, dispatch, rootState }: Context,
                          payload?: PatUntersuchung | PatUntersuchung[]): void {
            const loadingKey = Array.isArray(payload) ? 'printUntersuchungen' : 'printUntersuchung';

            commit('setLoadersUntersuchung', { [loadingKey]: true });

            const data = {
                untersuchungen: Array.isArray(payload)
                    ? payload.map(({ patuntersuchung_id }) => patuntersuchung_id)
                    : [payload?.patuntersuchung_id ?? state.selected_untersuchung.patimpfung_id],
                impfstelle_id: rootState.authentication.impfstelle_id,
                befund: false,
            };

            api.post('ivs/untersuchung', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    dispatch('loadImpfungen');
                    dispatch('globals/printDokument', { data }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersUntersuchung', { [loadingKey]: false }));
        },
        printUntersuchungMitBefund({ state, commit, dispatch, rootState }: Context,
                                   payload?: PatUntersuchung | PatUntersuchung[]): void {
            const loadingKey = Array.isArray(payload) ? 'printBefunde' : 'printBefund';

            commit('setLoadersUntersuchung', { [loadingKey]: true });

            const data = {
                untersuchungen: Array.isArray(payload)
                    ? payload.map(({ patuntersuchung_id }) => patuntersuchung_id)
                    : [payload?.patuntersuchung_id ?? state.selected_untersuchung.patimpfung_id],
                impfstelle_id: rootState.authentication.impfstelle_id,
                befund: true,
            };

            api.post('ivs/untersuchung', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    dispatch('loadImpfungen');
                    dispatch('globals/printDokument', { data }, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersUntersuchung', { [loadingKey]: false }));
        },
        sendUntersuchungMitBefund({ commit, rootState }: Context, untersuchungen: PatUntersuchung[]): void {
            commit('setLoadersUntersuchung', { email: true });

            const data = {
                untersuchungen: untersuchungen.map(({ patuntersuchung_id }) => patuntersuchung_id),
                impfstelle_id: rootState.authentication.impfstelle_id,
            };

            api.post('ivs/untersuchung/email', data)
                .then((response: AxiosResponse) => {
                    if (response.data.body === true) {
                        successSwal('Befund per Email', 'gesendet');
                    } else if (typeof response.data.body === 'string') {
                        warningSwal('Befund per Email', typeof response.data.body);
                    } else {
                        toastSwal('error', 'Befund per Email', 'fehlgeschlagen');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersUntersuchung', { email: false }));
        },
        setLoaders({ commit }: Context, payload: PatientLoaders): void {
            commit('setLoaders', payload);
        },
        findImpfpass({ commit }: Context, { svnr }: Patient): void {
            api.post('ivs/impfpass/patient', { svnr, system: true })
                .then((response: AxiosResponse) => {
                    console.log('findImpfpass:', response.data.body);

                    if (typeof response.data.body !== 'string') {
                        commit('setElgaPatient', response.data.body);
                        commit('setDialogElgaPatient', true);
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    toastSwal('error', 'e-Impfpass', 'Daten konnten nicht von ELGA abgerufen werden');
                })
                .finally(() => commit('setLoadersElga', { patient: false }));
        },
        searchImpfpass({ commit }: Context, patient: Patient): Promise<boolean> {
            commit('setLoadersElga', { search: true });

            return api.post('ivs/impfpass/searchpatient', patient)
                .then((response: AxiosResponse) => {
                    console.log('searchImpfpass:', response.data.body);
                    commit('setLoadersElga', { search: false });

                    if (typeof response.data.body !== 'string') {
                        commit('setElgaPatienten', response.data.body);
                        commit('setDialogElgaSuche', true);
                        return true;
                    } else {
                        errorSwal('Fehler', response.data.body);
                        return false;
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    toastSwal('error', 'e-Impfpass', 'Daten konnten nicht von ELGA abgerufen werden');
                    commit('setLoadersElga', { search: false });
                    return false;
                });
        },
        autoFindImpfpass({ commit }: Context, { svnr }: Patient): void {
            api.post('ivs/impfpass/patient', { svnr })
                .then((response: AxiosResponse) => {
                    console.log('autoFindImpfpass:', response.data.body);

                    if (typeof response.data.body !== 'string') {
                        commit('setElgaPatient', response.data.body);
                        commit('setDialogElgaPatient', true);
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    toastSwal('error', 'e-Impfpass', 'Daten konnten nicht von ELGA abgerufen werden');
                })
                .finally(() => commit('setLoadersElga', { patient: false }));
        },
        saveImpfpass({ commit, dispatch, rootState }: Context, payload: { patient: Patient }): Promise<boolean> {
            commit('setLoadersElga', { save: true });

            const data = {
                patnr: payload.patient.patnr,
                bpk_gh: payload.patient.bpk_gh,
                svnr: payload.patient.svnr,
                strasse: payload.patient.strasse,
                plz: payload.patient.plz,
                ort: payload.patient.ort,
                land: payload.patient.land,
                impfstelle: rootState.authentication.impfstelle_id,
            };

            return api.post('ivs/impfpass/save', data)
                .then((response: AxiosResponse) => {
                    console.log('saveImpfpass:', response.data.body);
                    commit('setLoadersElga', { save: false });

                    if (typeof response.data.body !== 'string') {
                        commit('setDialogElgaPatient', false);
                        dispatch('syncImpfpass', payload.patient);
                        successSwal('e-Impfpass', 'wird synchronisiert');
                        commit('updatePatientData', response.data.body);

                        return true;
                    } else {
                        errorSwal('Fehler', response.data.body);
                        return false;
                    }
                })
                .catch((error: AxiosError<{ body?: any }>) => {
                    commit('setLoadersElga', { save: false });
                    console.error(error);
                    errorSwal('Fehler', error.response?.data?.body ?? error.message);
                    return false;
                });
        },
        syncImpfpass({ commit, dispatch, rootState }: Context, { patnr }: Patient): void {
            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/sync', { patnr, system: true, arbeitsplatz_id, impfstelle_id })
                .then((response: AxiosResponse) => {
                    console.log('syncImpfpass:', response.data.body);

                    if (typeof response.data.body !== 'string' && response.data.body?.found) {
                        dispatch('loadImpfungen');
                        successSwal('e-Impfpass', 'synchronisiert');
                    } else {
                        const errorMsg = response.data.body?.errors || response.data.body;
                        console.warn('syncImpfpass failed:', errorMsg);
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    toastSwal('error', 'e-Impfpass', 'Daten konnten nicht von ELGA abgerufen werden');
                })
                .finally(() => commit('setLoadersElga', { patient: false }));
        },
        autoSyncImpfpass({ commit, dispatch, rootState }: Context, { patnr }: Patient): void {
            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/impfpass/sync', { patnr, arbeitsplatz_id, impfstelle_id })
                .then((response: AxiosResponse) => {
                    console.log('autoSyncImpfpass:', response.data.body);

                    if (typeof response.data.body !== 'string') {
                        dispatch('loadImpfungen');
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    toastSwal('error', 'e-Impfpass', 'Daten konnten nicht von ELGA abgerufen werden');
                })
                .finally(() => commit('setLoadersElga', { patient: false }));
        },
        updateDekurs({ commit }: Context, payload: { patnr: number; dekurs: string | null }): void {
            commit('setLoadersDekurs', { save: true });

            api.post('ivs/patienten/dekurs', payload)
                .then((response: AxiosResponse) => {
                    commit('setDialogDekurs', false);
                    commit('setPatientDekurs', response.data.body.dekurs);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersDekurs', { save: false }));
        },
        waitPatient({ commit, dispatch, rootState }: Context,
                    payload: { patnr: number, bemerkungen: string | null, folgetermin: string | null }): void {
            commit('setLoadersWarteliste', { save: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('ivs/warteliste/create', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Warteliste', response.data.body ?? 'Fehler beim Speichern.');
                    } else {
                        successSwal('Warteliste', 'gespeichert');
                        commit('setDialogWarteliste', false);
                    }

                    dispatch('warteliste/loadWarteliste', null, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersWarteliste', { save: false }));
        },
        updateWaitPatient({ commit, dispatch, rootState }: Context, payload:
            { aufnahme: string | null, patnr: number | null, bemerkungen: string | null, folgetermin: string | null; }): void {
            commit('setLoadersWarteliste', { save: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('ivs/warteliste/updateAnmerkungen', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Warteliste', response.data.body ?? 'Fehler beim Aktualisieren.');
                    } else {
                        successSwal('Warteliste', 'aktualisiert');
                        commit('setDialogWarteliste', false);
                    }

                    dispatch('warteliste/loadWarteliste', null, { root: true });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersWarteliste', { save: false }));
        },
        getCardInfo({ commit, rootState, dispatch }: Context, payload? : {onlyInfo: boolean }): void {
            const arbeitsplatz_id = rootState.session.arbeitsplatz;
            if (!arbeitsplatz_id) {
                setTimeout(() => commit('globals/setDialogArbeitsplatzhinweis', true, { root: true }), 100);
                return;
            }

            if (payload && payload.onlyInfo) {
                commit('setLoadersEcardInfo', { info: true });
            } else {
                commit('setLoadersEcard', { info: true });
            }

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                arbeitsplatz_id,
            };

            api.post('ivs/cards/info', data)
                .then(({ data }: AxiosResponse<LoadCardInfoData>) => {
                    if (data.body.patienten.length === 0) {
                        dispatch('patientContact', { svnr: data.body.card.number });
                    }

                    if (payload) {
                        data.body.onlyInfo = payload.onlyInfo;
                    }

                    commit('setCardInfo', data);
                })
                .catch((error) => {
                    console.error(error);

                    const errorResponse = typeof error.response?.data?.body === 'string'
                        ? error.response?.data?.body
                        : null;

                    toastSwal('error', 'e-Card', errorResponse ?? 'Daten konnten nicht abgerufen werden');

                    commit('patientNew', true);
                })
                .finally(() => {
                    commit('setLoadersEcard', { info: false });
                    commit('setLoadersEcardInfo', { info: false });
                });
        },
        setSelectedOffenNext({ commit }: Context): void {
            commit('setSelectedOffenNext');
        },
        updateImpferinnerung({ commit, rootState }: Context, { patimpfung_id, impferinnerung }: Impfung): void {
            commit('setLoadersImpfungImpferinnerung', true);

            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/patimpfungen/impferinnerung', { patimpfung_id, impferinnerung, impfstelle_id })
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        commit('setSelectedImpfeintrag', response.data.body);
                        successSwal('Impfung', 'gespeichert');
                    } else {
                        toastSwal('error', 'Impfung', 'Fehler beim Speichern');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersImpfungImpferinnerung', false));
        },
        resetErinnerungGesendet({ commit, rootState }: Context, { patimpfung_id }: Impfung): void {
            commit('setLoadersImpfungErinnerungGesendetReset', true);

            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/patimpfungen/erinnerunggesendetzuruecksetzen', { patimpfung_id, impfstelle_id })
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        commit('setSelectedImpfeintrag', response.data.body);
                        successSwal('Impfung', 'gespeichert');
                    } else {
                        toastSwal('error', 'Impfung', 'Fehler beim Speichern');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersImpfungErinnerungGesendetReset', false));
        },
        updateImpfungAbrechnung({ commit, rootState, dispatch }: Context, { patimpfung_id, abrechnung, hcw }: Impfung): void {
            commit('setLoadersImpfungAbrechnung', true);

            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/patimpfungen/abrechnung', { patimpfung_id, abrechnung, impfstelle_id, hcw })
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        commit('setSelectedImpfeintrag', response.data.body);
                        successSwal('Impfung', 'gespeichert');
                        dispatch('loadImpfungen');
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Fehler beim Speichern');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersImpfungAbrechnung', false));
        },
        updateLeistungAbrechnung({ commit, rootState, dispatch }: Context, { patimpfung_id, abrechnung, hcw }: Impfung): void {
            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/patimpfungen/abrechnung', { patimpfung_id, abrechnung, impfstelle_id, hcw })
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        dispatch('loadImpfungen');
                        successSwal('Leistung', 'gespeichert');
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Fehler beim Speichern');
                    }
                })
                .catch(console.error);
        },
        updateUntersuchungAbrechnung({ commit, rootState }: Context, payload: { patimpfung_id: number, abrechnung: number, hcw: string }): void {
            commit('setLoadersUntersuchungAbrechnung', true);

            const { impfstelle_id } = rootState.authentication;

            api.post('ivs/patimpfungen/abrechnung', { patimpfung_id: payload.patimpfung_id, abrechnung: payload.abrechnung, impfstelle_id, hcw: payload.hcw })
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        //commit('setSelectedImpfeintrag', response.data.body);
                        successSwal('Untersuchung', 'gespeichert');
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Fehler beim Speichern');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersUntersuchungAbrechnung', false));
        },
        loadUntersuchungen({ commit, state }: Context): void {
            const patnr = state.patientData?.patnr;
            if (!patnr) {
                return;
            }

            commit('setLoaders', { untersuchungen: true });

            api.post('ivs/patuntersuchungen', { patnr })
                .then((response: AxiosResponse) => {
                    if (patnr === state.patientData?.patnr) {
                        commit('setUntersuchungen', response.data.body);
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { untersuchungen: false }));
        },
        loadLastAnamnese({ commit, state }: Context, payload?: { patnr: number}): void {
            const patnr = payload ? payload.patnr : state.patientData?.patnr;
            if (!patnr) {
                return;
            }

            api.post('ivs/patimpfungen/checkanamnese', {patnr: patnr})
                .then((response: AxiosResponse) => {
                    commit('setPatientLastAnamnese', response.data.body);
                })
                .catch(() => {
                    commit('setPatientLastAnamnese', null);
                });
        },
    },
    mutations: {
        ...patientrechnung.mutations,
        ...patientreiseplaene.mutations,
        ...patientimpfungen.mutations,
        patientDetail(state: PatientState, visible: boolean): void {
            if (state.patientData) {
                state.dialogs.patientDetail = visible;
            }
        },
        patientNew(state: PatientState, visible: boolean): void {
            state.patientMitAdresse = false;
            state.dialogs.patientNew = visible;
        },
        setPatientMitAdresse(state: PatientState): void {
            state.patientMitAdresse = true;
        },
        setPatientMitAdresseUndPlan(state: PatientState, payload = true): void {
            state.patientMitAdresseUndPlan = payload;
        },
        patientDruck(state: PatientState, visible: boolean): void {
            state.dialogs.patientDruckDetail = visible;
        },
        patientEtiketten(state: PatientState, visible: boolean): void {
            state.dialogs.patientEtikettenDetail = visible;
        },
        patientNeuMitAdresse(state: PatientState, visible: boolean): void {
            state.dialogs.patientNew = visible;
        },
        patientNeuMitAdresseUndPlan(state: PatientState, visible: boolean): void {
            state.dialogs.patientNew = visible;
            state.patientMitAdresse = true;
        },
        updatePatientData(state: PatientState, patient: Partial<Patient>): void {
            state.patientData = new Patient(patient);
        },
        updateDistinctBefundwerttexte(state: PatientState, payload: string[]): void {
            state.distinctBefundwerttexte = payload;
        },
        updateLoadedPatientData(state: PatientState, patient: Partial<Patient>): void {
            state.loadedPatientData = new Patient(patient);
        },
        setElgaPatient(state: PatientState, body: { result: ElgaPatient[] | undefined } | undefined): void {
            const [patient] = body?.result ?? [];
            state.elgaPatient = new ElgaPatient(patient);
        },
        setElgaPatienten(state: PatientState, body: { result: ElgaPatient[] | undefined } | undefined): void {
            state.elgaPatienten = (body?.result ?? []).map((data) => new ElgaPatient(data));
        },
        setElgaPatientAnlage(state: PatientState, patient: Partial<ElgaPatient>): void {
            state.elgaPatientAnlage = new ElgaPatient(patient);
        },
        setZahlendePatienten(state: PatientState, patienten: Warteliste[]): void {
            state.zahlendePatienten = patienten.map((data) => new Warteliste(data));
        },
        setCardInfo(state: PatientState, { body }: LoadCardInfoData): void {
            state.cardInfo = new CardInfo(body.card);
            state.cardInfoPatienten = body.patienten.map((data) => new Patient(data));

            if (body.onlyInfo) {
                if (state.cardInfoPatienten.length !== 0) {
                    state.dialogs.ecardInfo = true;
                } else {
                    state.patientMitAdresse = false;
                    state.dialogs.patientNew = true;
                }
            } else {
                if (state.cardInfoPatienten.length !== 0) {
                    state.dialogs.ecard = true;
                } else {
                    state.patientMitAdresse = false;
                    state.dialogs.patientNew = true;
                }
            }
        },
        setDialogText(state: PatientState, visible: boolean): void {
            state.dialogs.texteintrag = visible;
            if (!visible) {
                state.selected_text = new Karteieintrag();
            }
        },
        setDialogRezept(state: PatientState, visible: boolean): void {
            state.dialogs.rezept = visible;
            if (!visible) {
                state.selected_rezepteintrag = new Karteieintrag();
            }
        },
        setDialogLeistung(state: PatientState, visible: boolean): void {
            state.dialogs.leistung = visible;
            if (!visible) {
                state.selected_leistungseintrag = new Karteieintrag();
            }
        },
        setDialogDiagnose(state: PatientState, visible: boolean): void {
            state.dialogs.diagnose = visible;
            if (!visible) {
                state.selected_diagnose = new Karteieintrag();
            }
        },
        setDialogAnamnese(state: PatientState, visible: boolean): void {
            state.dialogs.anamnese = visible;
            if (!visible) {
                state.selected_anamnese = new Karteieintrag();
            }
        },
        setDialogAnamneseListItem(state: PatientState, check: boolean): void {
            state.dialogs.anamneselistitem = check;
        },
        setDialogBrief(state: PatientState, visible: boolean): void {
            state.dialogs.brief = visible;
            if (!visible) {
                state.selected_brief = new Karteieintrag();
            }
        },
        setDialogUntersuchung(state: PatientState, visible: boolean): void {
            state.dialogs.untersuchung = visible;
            if (!visible) {
                state.selected_untersuchung = new Karteieintrag();
            }
        },
        setDialogWarteliste(state: PatientState, visible: boolean): void {
            state.dialogs.warteliste = visible;
        },
        setDialogElgaPatient(state: PatientState, visible: boolean): void {
            state.dialogs.elgaPatient = visible;
        },
        setDialogElgaSuche(state: PatientState, visible: boolean): void {
            state.dialogs.elgaSuche = visible;
        },
        setTempPatientData(state: PatientState, patient: Patient): void {
            state.tempPatientData = patient;
        },
        setSelectedText(state: PatientState, payload: Karteieintrag): void {
            state.selected_text = new Karteieintrag(payload);
        },
        setSelectedRezepteintrag(state: PatientState, payload: Karteieintrag): void {
            state.selected_rezepteintrag = new Karteieintrag(payload);
        },
        setSelectedUntersuchung(state: PatientState, payload: Karteieintrag): void {
            state.selected_untersuchung = new Karteieintrag(payload);
        },
        setSelectedDiagnose(state: PatientState, payload: Karteieintrag): void {
            state.selected_diagnose = new Karteieintrag(payload);
        },
        setSelectedAnamnese(state: PatientState, payload: Karteieintrag): void {
            state.selected_anamnese = new Karteieintrag(payload);
        },
        setSelectedOffen(state: PatientState, payload: PlanItem<Planimpfung | PatUntersuchung>[]): void {
            state.selected_offen = payload.map((item) => item.clone());
        },
        setSelectedOffenNext(state: PatientState): void {
            const patientState = state as ExtendedPatientState;

            const item = state.selected_offen.shift();
            if (item) {
                if (item.isImpfung) {
                    patientState.selected_offeneImpfung = item.asImpfung;

                    patientState.patientImpfungNew = new Impfung({
                        impfstoff_id: patientState.selected_offeneImpfung.impfstoff_id,
                        patplan_id: patientState.selected_offeneImpfung.patplan_id,
                        impfdatum: patientState.selected_offeneImpfung.impfdatum_geplant?.split(' ')[0],
                        teilimpfungsnr: patientState.selected_offeneImpfung.teilimpfungsnr,
                        patplanimpfstoff_id: patientState.selected_offeneImpfung.patplanimpfstoff_id,
                        impfschema_code: patientState.selected_offeneImpfung.impfschema_code,
                    });

                    state.dialogs.impfungNew = true;
                } else if (item.isUntersuchung) {
                    state.selected_untersuchung = new Karteieintrag({ untersuchung: item.asUntersuchung });
                    state.dialogs.untersuchung = true;
                }
            }
        },
        setUntersuchungen(state: PatientState, payload: Partial<PatUntersuchung>[]): void {
            state.untersuchungen = payload.map((data) => new PatUntersuchung(data));
        },
        setPatienten(state: PatientState, payload: Patient[]): void {
            state.patienten = payload.map((data) => new Patient(data));
        },
        setDialogPatientUebertragung(state: PatientState, visible: boolean): void {
            state.dialogs.patientuebertragung = visible;
        },
        setDialogSsnoSearch(state: PatientState, visible: boolean): void {
            state.dialogs.ssnoSearch = visible;
        },
        setDialogEcard(state: PatientState, visible: boolean): void {
            state.dialogs.ecard = visible;
        },
        setDialogEcardInfo(state: PatientState, visible: boolean): void {
            state.dialogs.ecardInfo = visible;
        },
        setDialogDekurs(state: PatientState, visible: boolean): void {
            state.dialogs.dekurs = visible;
        },
        setDialogZurKassa(state: PatientState, visible: boolean): void {
            state.dialogs.zurKassa = visible;
        },
        setPatientDekurs(state: PatientState, dekurs: string): void {
            if (state.patientData) {
                state.patientData.dekurs = dekurs;
            }
        },
        setLoadersPatient(state: PatientState, loading: boolean): void {
            state.loaders.patient = loading;
        },
        setLoaders(state: PatientState, loaders: PatientLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
        setLoadersLeistung(state: PatientState, loaders: { save?: boolean; delete?: boolean }): void {
            state.loaders.leistung = {
                ...state.loaders.leistung,
                ...loaders,
            };
        },
        setLoadersDiagnose(state: PatientState, loaders: { save?: boolean; delete?: boolean }): void {
            state.loaders.diagnose = {
                ...state.loaders.diagnose,
                ...loaders,
            };
        },
        setLoadersAnamnese(state: PatientState, loaders: { save?: boolean; delete?: boolean }): void {
            state.loaders.anamnese = {
                ...state.loaders.anamnese,
                ...loaders,
            };
        },
        setLoadersRezept(state: PatientState, loaders: { save?: boolean; print?: boolean; delete?: boolean }): void {
            state.loaders.rezepteintrag = {
                ...state.loaders.rezepteintrag,
                ...loaders,
            };
        },
        setLoadersBrief(state: PatientState, loaders: BriefLoaders): void {
            state.loaders.brief = {
                ...state.loaders.brief,
                ...loaders,
            };
        },
        setLoadersUntersuchungLoaders(state: PatientState, loaders: UntersuchungLoaders): void {
            state.loaders.untersuchung = {
                ...state.loaders.untersuchung,
                ...loaders,
            };
        },
        setLoadersBeleg(state: PatientState, loaders: BelegLoaders): void {
            state.loaders.beleg = {
                ...state.loaders.beleg,
                ...loaders,
            };
        },
        setLoadersElga(state: PatientState, loaders: ElgaLoaders): void {
            state.loaders.elga = {
                ...state.loaders.elga,
                ...loaders,
            };
        },
        setLoadersEcardInfo(state: PatientState, loaders: { info?: boolean }): void {
            state.loaders.ecardInfo = {
                ...state.loaders.ecardInfo,
                ...loaders,
            };
        },
        setLoadersDekurs(state: PatientState, loaders: { save?: boolean }): void {
            state.loaders.dekurs = {
                ...state.loaders.dekurs,
                ...loaders,
            };
        },
        setLoadersUntersuchung(state: PatientState, loaders: UntersuchungLoaders): void {
            state.loaders.untersuchung = {
                ...state.loaders.untersuchung,
                ...loaders,
            };
        },
        setLoadersWarteliste(state: PatientState, loaders: { save?: boolean }): void {
            state.loaders.warteliste = {
                ...state.loaders.warteliste,
                ...loaders,
            };
        },
        setLoadersEcard(state: PatientState, loaders: { info?: boolean }): void {
            state.loaders.ecard = {
                ...state.loaders.ecard,
                ...loaders,
            };
        },
        setLoadersText(state: PatientState,
                       loaders: { save?: boolean; delete?: boolean; download?: boolean; downloads?: number[]; }): void {
            state.loaders.text = {
                ...state.loaders.text,
                ...loaders,
            };
        },
        setLoadersTextDownloads(state: PatientState, payload: { patimpfung_id: number; download: boolean }): void {
            const { patimpfung_id, download } = payload;
            if (download) {
                state.loaders.text.downloads.push(patimpfung_id);
            } else {
                state.loaders.text.downloads.splice(state.loaders.text.downloads.indexOf(patimpfung_id), 1);
            }
        },
        setLoadersImpfungImpferinnerung(state: PatientState, loading: boolean): void {
            state.loaders.impfungImpferinnerung = loading;
        },
        setLoadersImpfungErinnerungGesendetReset(state: PatientState, loading: boolean): void {
            state.loaders.impfungErinnerungGesendetReset = loading;
        },
        setLoadersImpfungAbrechnung(state: PatientState, loading: boolean): void {
            state.loaders.impfungAbrechnung = loading;
        },
        setLoadersUntersuchungAbrechnung(state: PatientState, loading: boolean): void {
            state.loaders.untersuchungAbrechnung = loading;
        },
       setDialogSammelrechnung(state: PatientState, visible: boolean): void {
            state.dialogs.sammelrechnung = visible;
        },
        setShowAllePatientInfo(state: PatientState, visible: boolean): void {
            state.showAllePatientInfo = visible;
        },
        setPatientLastAnamnese(state: PatientState, anamnese: Impfung): void {
            state.patientLastAnamnese = new Impfung(anamnese);
        },
    },
    getters: {
        ...(patientrechnung as unknown as Module<PatientState, RootState>).getters,
        ...(patientreiseplaene as unknown as Module<PatientState, RootState>).getters,
        ...(patientimpfungen as unknown as Module<PatientState, RootState>).getters,
    },
};
